
import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  AlertCircle,
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  Download,
  ExternalLink,
  FileCheck2,
  FileText,
  GraduationCap,
  Info,
  Loader2,
  Printer,
  RefreshCw,
  School,
  ShieldCheck,
} from "lucide-react";

import { api, resolveMediaUrl } from "../lib/api";

/* ==========================================
   HELPER FUNCTIONS
========================================== */

function formatDate(value) {
  if (!value) return "—";

  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [year, month, day] = value
      .split("-")
      .map(Number);

    return new Date(
      year,
      month - 1,
      day
    ).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? String(value)
    : date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
}

const inputClass =
  "w-full rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3 pl-11 text-sm font-normal text-slate-800 outline-none transition focus:border-red-500 focus:bg-white focus:ring-4 focus:ring-red-50";

/* ==========================================
   STUDENT DETAILS CARD
========================================== */

function StudentDetail({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-[#FCFAF8] px-4 py-3.5 transition-colors hover:border-red-100 hover:bg-red-50/30">
      <p className="text-xs font-normal text-slate-500">
        {label}
      </p>

      <p className="mt-1.5 break-words text-sm font-medium text-slate-900">
        {value || "—"}
      </p>
    </div>
  );
}

/* ==========================================
   MAIN COMPONENT
========================================== */

export default function StudentTC() {
  const [formData, setFormData] = useState({
    registrationNo: "",
    dob: "",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const [certificateData, setCertificateData] =
    useState(null);

  const [requestError, setRequestError] = useState("");
  const [pdfError, setPdfError] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const pdfUrl = certificateData?.file
    ? resolveMediaUrl(certificateData.file)
    : "";

  /* ==========================================
     FORM HANDLERS
  ========================================== */

  function handleChange(event) {
    const { name, value } = event.target;

    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));

    setErrors((previous) => ({
      ...previous,
      [name]: "",
    }));

    setRequestError("");
  }

  function validate() {
    const nextErrors = {};

    if (!formData.registrationNo.trim()) {
      nextErrors.registrationNo =
        "Registration Number is required.";
    }

    if (!formData.dob) {
      nextErrors.dob =
        "Date of Birth is required.";
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (isLoading || !validate()) return;

    setIsLoading(true);
    setRequestError("");
    setCertificateData(null);
    setPdfError(false);

    try {
      const response =
        await api.verifyTransferCertificate({
          registrationNo: formData.registrationNo
            .trim()
            .toUpperCase(),
          dateOfBirth: formData.dob,
        });

      if (!response?.data) {
        throw new Error(
          "Transfer Certificate information was not received."
        );
      }

      setCertificateData(response.data);
    } catch (error) {
      setCertificateData(null);

      setRequestError(
        error?.message ||
          "Unable to verify Transfer Certificate."
      );
    } finally {
      setIsLoading(false);
    }
  }

  function handleReset() {
    setFormData({
      registrationNo: "",
      dob: "",
    });

    setErrors({});
    setCertificateData(null);
    setRequestError("");
    setPdfError(false);
    setDownloading(false);
  }

  /* ==========================================
     PDF ACTIONS
  ========================================== */

  async function handleDownload() {
    if (!pdfUrl || downloading) return;

    setDownloading(true);

    try {
      const response = await fetch(pdfUrl);

      if (!response.ok) {
        throw new Error(
          "Unable to download Transfer Certificate."
        );
      }

      const blob = await response.blob();

      const objectUrl =
        window.URL.createObjectURL(blob);

      const link = document.createElement("a");

      link.href = objectUrl;

      link.download = `TC-${
        certificateData.tcNo || "Certificate"
      }.pdf`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      window.setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 1000);
    } catch (error) {
      window.open(
        pdfUrl,
        "_blank",
        "noopener,noreferrer"
      );
    } finally {
      setDownloading(false);
    }
  }

  function handlePrint() {
    if (!pdfUrl) return;

    window.open(
      pdfUrl,
      "_blank",
      "noopener,noreferrer"
    );
  }

  /* ==========================================
     PAGE
  ========================================== */

  return (
    <section className="relative min-h-screen overflow-hidden bg-[#FFFCF9] pb-16">

      {/* BACKGROUND DECORATION */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -left-32 top-[380px] h-80 w-80 rounded-full bg-red-100/50 blur-3xl" />

        <div className="absolute -right-32 bottom-10 h-96 w-96 rounded-full bg-amber-100/50 blur-3xl" />

        <div
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage:
              "radial-gradient(#991b1b 1px, transparent 1px)",
            backgroundSize: "27px 27px",
          }}
        />
      </div>

      {/* PAGE HERO */}
      <div className="relative overflow-hidden bg-gradient-to-r from-red-900 via-red-800 to-red-700 px-4 pb-24 pt-12 text-white sm:px-6 lg:px-8">

        <div className="pointer-events-none absolute -right-20 -top-24 h-72 w-72 rounded-full border-[35px] border-white/5" />

        <div className="pointer-events-none absolute bottom-0 left-1/3 h-40 w-40 rounded-full bg-amber-300/10 blur-3xl" />

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
          className="relative mx-auto max-w-5xl text-center"
        >
          <span className="inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-white/10 px-4 py-2 text-[11px] font-normal tracking-wide text-amber-100">
            <GraduationCap size={16} />
            Student Services
          </span>

          <h1 className="mt-5 font-serif text-[31px] font-medium leading-tight sm:text-[41px] lg:text-[48px]">
            Transfer{" "}
            <span className="text-amber-300">
              Certificate
            </span>
          </h1>

          <div className="mx-auto mt-4 h-1 w-20 rounded-full bg-amber-400" />

          <p className="mx-auto mt-5 max-w-xl text-sm font-normal leading-7 text-white/85 sm:text-[15px]">
            Verify your student details and access
            your Transfer Certificate securely.
          </p>
        </motion.div>
      </div>

      {/* MAIN CONTENT */}
      <div className="relative z-10 mx-auto -mt-12 max-w-5xl px-4 sm:px-6 lg:px-8">

        <AnimatePresence mode="wait">
          {!certificateData ? (
            /* ======================================
               VERIFICATION FORM
            ====================================== */

            <motion.div
              key="verification-form"
              initial={{
                opacity: 0,
                y: 18,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              exit={{
                opacity: 0,
                y: -10,
              }}
              transition={{
                duration: 0.3,
              }}
              className="mx-auto max-w-3xl"
            >
              <div className="overflow-hidden rounded-[24px] border border-red-100 bg-white shadow-[0_20px_55px_rgba(80,30,30,0.1)]">

                {/* FORM HEADER */}
                <div className="relative overflow-hidden border-b border-red-100 bg-gradient-to-r from-red-50 via-white to-amber-50 px-5 py-5 sm:px-8">

                  <div className="pointer-events-none absolute -right-8 -top-12 h-28 w-28 rounded-full border-[18px] border-red-100/50" />

                  <div className="relative flex items-center gap-4">

                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-800 text-white shadow-sm">
                      <FileCheck2 size={23} />
                    </div>

                    <div>
                      <h2 className="font-serif text-xl font-medium text-slate-900 sm:text-2xl">
                        Student Verification
                      </h2>

                      <p className="mt-1 text-xs font-normal leading-5 text-slate-500 sm:text-sm">
                        Enter your registration number and date of birth.
                      </p>
                    </div>
                  </div>
                </div>

                {/* FORM BODY */}
                <form
                  onSubmit={handleSubmit}
                  className="p-5 sm:p-8"
                >

                  {requestError && (
                    <div
                      role="alert"
                      className="mb-5 flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-normal leading-6 text-red-700"
                    >
                      <AlertCircle
                        size={18}
                        className="mt-0.5 shrink-0"
                      />

                      <span>{requestError}</span>
                    </div>
                  )}

                  {/* INPUTS */}
                  <div className="grid gap-5 sm:grid-cols-2">

                    {/* REGISTRATION NUMBER */}
                    <div>
                      <label
                        htmlFor="tc-registration"
                        className="mb-2 block text-sm font-medium text-slate-700"
                      >
                        Registration Number{" "}
                        <span className="text-red-600">*</span>
                      </label>

                      <div className="relative">
                        <FileText
                          size={19}
                          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-red-700"
                        />

                        <input
                          id="tc-registration"
                          type="text"
                          name="registrationNo"
                          value={formData.registrationNo}
                          onChange={handleChange}
                          placeholder="Enter registration number"
                          autoComplete="off"
                          aria-invalid={Boolean(
                            errors.registrationNo
                          )}
                          className={`${inputClass} ${
                            errors.registrationNo
                              ? "border-red-500"
                              : ""
                          }`}
                        />
                      </div>

                      {errors.registrationNo && (
                        <p className="mt-2 flex items-center gap-1.5 text-xs text-red-600">
                          <AlertCircle size={13} />
                          {errors.registrationNo}
                        </p>
                      )}
                    </div>

                    {/* DATE OF BIRTH */}
                    <div>
                      <label
                        htmlFor="tc-dob"
                        className="mb-2 block text-sm font-medium text-slate-700"
                      >
                        Date of Birth{" "}
                        <span className="text-red-600">*</span>
                      </label>

                      <div className="relative">
                        <CalendarDays
                          size={19}
                          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-red-700"
                        />

                        <input
                          id="tc-dob"
                          type="date"
                          name="dob"
                          value={formData.dob}
                          onChange={handleChange}
                          aria-invalid={Boolean(errors.dob)}
                          className={`${inputClass} ${
                            errors.dob
                              ? "border-red-500"
                              : ""
                          }`}
                        />
                      </div>

                      {errors.dob && (
                        <p className="mt-2 flex items-center gap-1.5 text-xs text-red-600">
                          <AlertCircle size={13} />
                          {errors.dob}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* BUTTONS */}
                  <div className="mt-7 flex flex-col gap-3 sm:flex-row">

                    <button
                      type="submit"
                      disabled={isLoading}
                      className="group inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-red-900 to-red-700 px-5 py-3.5 text-sm font-medium text-white shadow-md shadow-red-900/10 transition hover:from-red-800 hover:to-red-600 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {isLoading ? (
                        <>
                          <Loader2
                            size={18}
                            className="animate-spin"
                          />
                          Verifying Details...
                        </>
                      ) : (
                        <>
                          <FileCheck2 size={18} />
                          Get Transfer Certificate

                          <ArrowRight
                            size={17}
                            className="transition-transform group-hover:translate-x-1"
                          />
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      onClick={handleReset}
                      disabled={isLoading}
                      className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white px-6 py-3.5 text-sm font-normal text-slate-600 transition hover:border-red-200 hover:bg-red-50 hover:text-red-800 disabled:opacity-60"
                    >
                      <RefreshCw size={16} />
                      Reset
                    </button>
                  </div>

                  {/* HELP TEXT */}
                  <div className="mt-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3.5">
                    <Info
                      size={18}
                      className="mt-0.5 shrink-0 text-amber-700"
                    />

                    <p className="text-xs font-normal leading-6 text-slate-600">
                      Your registration number is provided
                      at the time of admission. Please
                      contact the school office if you
                      need assistance.
                    </p>
                  </div>
                </form>
              </div>

              {/* BOTTOM NOTE */}
              <div className="mt-5 flex items-center justify-center gap-2 text-center text-xs font-normal text-slate-500">
                <ShieldCheck
                  size={16}
                  className="text-red-800"
                />

                Enter the student details exactly as
                recorded by the school.
              </div>
            </motion.div>
          ) : (
            /* ======================================
               VERIFIED CERTIFICATE
            ====================================== */

            <motion.div
              key="verified-certificate"
              initial={{
                opacity: 0,
                y: 18,
              }}
              animate={{
                opacity: 1,
                y: 0,
              }}
              exit={{
                opacity: 0,
                y: -10,
              }}
              transition={{
                duration: 0.3,
              }}
              className="space-y-6"
            >

              {/* STUDENT DETAILS CARD */}
              <div className="overflow-hidden rounded-[24px] border border-red-100 bg-white shadow-[0_14px_40px_rgba(80,30,30,0.07)]">

                {/* VERIFIED HEADER */}
                <div className="relative overflow-hidden bg-gradient-to-r from-red-900 to-red-700 px-5 py-6 text-white sm:px-8">

                  <div className="pointer-events-none absolute -right-10 -top-12 h-40 w-40 rounded-full border-[24px] border-white/10" />

                  <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">

                    <div className="flex items-center gap-4">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/15">
                        <School size={24} />
                      </div>

                      <div>
                        <h2 className="font-serif text-lg font-medium leading-snug sm:text-2xl">
                          {certificateData.schoolName ||
                            "ST. XAVIER'S PUBLIC SCHOOL, BELA"}
                        </h2>

                        <p className="mt-1 text-xs text-white/75">
                          Transfer Certificate Verification
                        </p>
                      </div>
                    </div>

                    <span className="inline-flex w-fit items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-2 text-xs font-normal text-white">
                      <CheckCircle2
                        size={16}
                        className="text-amber-300"
                      />
                      Student Record Verified
                    </span>
                  </div>
                </div>

                {/* STUDENT INFORMATION */}
                <div className="p-5 sm:p-7">
                  <div className="mb-5 flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-50 text-red-800">
                      <GraduationCap size={21} />
                    </div>

                    <div>
                      <h3 className="font-serif text-xl font-medium text-slate-900">
                        Student Details
                      </h3>

                      <p className="mt-0.5 text-xs text-slate-500">
                        Information from the school record
                      </p>
                    </div>
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">

                    <StudentDetail
                      label="Student Name"
                      value={certificateData.studentName}
                    />

                    <StudentDetail
                      label="Registration Number"
                      value={certificateData.registrationNo}
                    />

                    <StudentDetail
                      label="TC Number"
                      value={certificateData.tcNo}
                    />

                    <StudentDetail
                      label="Date of Birth"
                      value={formatDate(
                        certificateData.dateOfBirth
                      )}
                    />

                    <StudentDetail
                      label="Issue Date"
                      value={formatDate(
                        certificateData.issueDate
                      )}
                    />

                    <StudentDetail
                      label="Class"
                      value={certificateData.className}
                    />

                  </div>
                </div>
              </div>

              {/* PDF PREVIEW CARD */}
              <div className="overflow-hidden rounded-[24px] border border-red-100 bg-white shadow-[0_14px_40px_rgba(80,30,30,0.07)]">

                {/* PDF HEADER */}
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-red-100 bg-gradient-to-r from-red-50 via-white to-amber-50 px-5 py-4 sm:px-7">

                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-red-800 text-white">
                      <FileText size={22} />
                    </div>

                    <div>
                      <h3 className="font-serif text-lg font-medium text-slate-900 sm:text-xl">
                        Transfer Certificate PDF
                      </h3>

                      <p className="mt-1 text-xs font-normal text-slate-500">
                        Original document uploaded by the school
                      </p>
                    </div>
                  </div>

                  {pdfUrl && (
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
                      <FileCheck2 size={15} />
                      Document Linked
                    </span>
                  )}
                </div>

                {pdfUrl ? (
                  <div className="p-4 sm:p-6">

                    {/* ORIGINAL PDF PREVIEW */}
                    <div className="overflow-hidden rounded-xl border border-slate-200 bg-slate-100">
                      <iframe
                        key={pdfUrl}
                        src={pdfUrl}
                        title="Original Transfer Certificate PDF"
                        className="h-[470px] w-full border-0 sm:h-[620px] lg:h-[720px]"
                        onError={() => setPdfError(true)}
                      />
                    </div>

                    {pdfError && (
                      <div
                        role="alert"
                        className="mt-3 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-xs leading-6 text-amber-800"
                      >
                        <AlertCircle
                          size={16}
                          className="mt-1 shrink-0"
                        />

                        PDF preview could not be loaded.
                        Please open the document directly.
                      </div>
                    )}

                    {/* PDF ACTION BUTTONS */}
                    <div className="mt-5 flex flex-wrap gap-3">

                      <a
                        href={pdfUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-800 px-5 py-3 text-sm font-medium text-white transition hover:bg-red-900"
                      >
                        <ExternalLink size={17} />
                        View PDF
                      </a>

                      <button
                        type="button"
                        onClick={handleDownload}
                        disabled={downloading}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-200 bg-white px-5 py-3 text-sm font-medium text-red-800 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {downloading ? (
                          <Loader2
                            size={17}
                            className="animate-spin"
                          />
                        ) : (
                          <Download size={17} />
                        )}

                        {downloading
                          ? "Downloading..."
                          : "Download PDF"}
                      </button>

                      <button
                        type="button"
                        onClick={handlePrint}
                        className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-200 bg-slate-50 px-5 py-3 text-sm font-normal text-slate-700 transition hover:bg-slate-100"
                      >
                        <Printer size={17} />
                        Print PDF
                      </button>

                    </div>
                  </div>
                ) : (
                  /* PDF NOT AVAILABLE */
                  <div className="px-6 py-12 text-center">
                    <FileText
                      size={42}
                      className="mx-auto text-red-200"
                    />

                    <h4 className="mt-4 font-serif text-xl font-medium text-slate-900">
                      PDF Not Available
                    </h4>

                    <p className="mx-auto mt-3 max-w-lg text-sm font-normal leading-7 text-slate-500">
                      The student record was verified,
                      but no Transfer Certificate PDF
                      is associated with this record.
                      Please contact the school office
                      for assistance.
                    </p>
                  </div>
                )}

                {/* NEW VERIFICATION */}
                <div className="border-t border-slate-100 bg-slate-50/70 px-5 py-4 sm:px-7">
                  <button
                    type="button"
                    onClick={handleReset}
                    className="inline-flex items-center gap-2 rounded-xl border border-red-200 bg-white px-4 py-2.5 text-sm font-medium text-red-800 transition hover:bg-red-50"
                  >
                    <RefreshCw size={16} />
                    New Verification
                    <ArrowRight size={15} />
                  </button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
}
